import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import { useState } from 'react';

import { formatCents } from '../v2/shared';
import type { ActivityLine } from './rows';

const CHARGE_LINE_PREFIX = 'charge_line:';

/** The charge line behind an activity row, when there is one (gdn-3190).
 *
 * `entry:` ids are ledger entries — payments, adjustments, the events a
 * charge line went through. They have nothing to reverse, so they render
 * as history without a verb. Same filter the drawer's lifecycle rows used.
 */
export const chargeLineIdOf = (line: ActivityLine): string | null =>
  line.id.startsWith(CHARGE_LINE_PREFIX) ? line.id.slice(CHARGE_LINE_PREFIX.length) : null;

// Values mirror PatientBalanceTable's module constants (INK_FAINT, BG),
// which are themselves raw hex — this file matches the module it lives
// in rather than half-migrating one component to theme tokens. The tone
// map is forked from the drawer's LIFECYCLE_TONE_COLOR because the
// table's own Collected hover marks tone with a '✓' prefix and keeps no
// palette to import.
const TONE_COLOR: Record<ActivityLine['tone'], string> = {
  ok: '#16A34A',
  due: '#D97706',
  info: '#8b93a1',
};

interface Props {
  lines: ActivityLine[];
  /** The encounter these lines belong to, so a reversal is attributed to
   * it rather than to the account (gdn-3190). */
  encounterId: string;
  onReverseCharge?: (charge: {
    chargeLineId: string;
    amountCents: number;
    hasAppliedPayment?: boolean;
    label: string;
    encounterId?: string;
  }) => void;
}

/**
 * An encounter's charge lines, one business event per row (gdn-3190).
 *
 * The table's encounter rows roll several charge lines into one figure,
 * so there is no single line for a per-line verb to act on. This is that
 * missing surface — and deliberately not a promoted tooltip.
 *
 * The Collected column's hover renders the same `activity` data, but its
 * display is gated on `collectedCents > 0`. An encounter whose only
 * problem is an unpaid duplicate has collected nothing, so that hover
 * never appears — it would have offered the verb everywhere except the
 * case this exists for. The data was never gated; only that tooltip was.
 *
 * Rendered independently of collection state for exactly that reason.
 */
export const EncounterLinesPanel = ({ lines, encounterId, onReverseCharge }: Props) => (
  <Box sx={{ px: 2.5, py: 1, bgcolor: '#f7f8fa' }}>
    {lines.length === 0 ? (
      <Typography variant="caption" sx={{ color: TONE_COLOR.info }}>
        No charge activity on this visit yet.
      </Typography>
    ) : (
      lines.map(line => (
        <LinePanelRow
          key={line.id}
          line={line}
          encounterId={encounterId}
          onReverseCharge={onReverseCharge}
        />
      ))
    )}
  </Box>
);

const LinePanelRow = ({
  line,
  encounterId,
  onReverseCharge,
}: {
  line: ActivityLine;
  encounterId: string;
  onReverseCharge?: Props['onReverseCharge'];
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const chargeLineId = chargeLineIdOf(line);
  const canReverse = Boolean(chargeLineId) && Boolean(onReverseCharge);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 0.5,
        minHeight: 32,
      }}
    >
      <Typography variant="caption" sx={{ flex: 1, color: 'text.primary' }}>
        {line.label}
        {line.detail && (
          <Typography component="span" variant="caption" sx={{ color: TONE_COLOR.info, ml: 0.75 }}>
            {line.detail}
          </Typography>
        )}
      </Typography>
      <Typography variant="caption" sx={{ color: TONE_COLOR[line.tone], fontWeight: 600 }}>
        {formatCents(line.amount_cents)}
      </Typography>
      <Box sx={{ width: 28, display: 'flex', justifyContent: 'flex-end' }}>
        {canReverse && (
          <>
            <IconButton
              size="small"
              aria-label={`Actions for ${line.label}`}
              onClick={event => {
                // The row above is role="link" — every control in here
                // stops propagation so acting on a line never navigates.
                event.stopPropagation();
                setAnchorEl(event.currentTarget);
              }}
              sx={{ color: TONE_COLOR.info, p: 0.25 }}
            >
              <MoreVertIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
              onClick={event => event.stopPropagation()}
              onKeyDown={event => event.stopPropagation()}
            >
              <MenuItem
                onClick={() => {
                  setAnchorEl(null);
                  onReverseCharge?.({
                    chargeLineId: chargeLineId as string,
                    amountCents: line.amount_cents,
                    // Deliberately omitted: activity lines carry no
                    // payment state. tone 'ok' covers WRITTEN_OFF as
                    // well as PAID, so inferring paid-ness here would
                    // put a false money claim in a confirmation dialog
                    // for a line the server then refuses anyway.
                    label: line.label,
                    encounterId,
                  });
                }}
                sx={{ fontSize: '0.8125rem' }}
              >
                Reverse charge
              </MenuItem>
            </Menu>
          </>
        )}
      </Box>
    </Box>
  );
};
